import React, { useState, useLayoutEffect, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ImageFile from "../images/afrogebeya.png";
import "bootstrap/dist/css/bootstrap.min.css";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import LogoutButton from "./LogoutButton";

function Header({ hideExtras = false }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);

  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const hideTimerRef = useRef(null);

  const { auth, isAuthenticated } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const displayName = auth?.firstName ?? "User";

  // Determine path prefix: "/buyer" or "/seller", default to "/buyer"
  const pathSegment = location.pathname.split("/")[1]?.toLowerCase();
  const prefix = pathSegment === "seller" ? "/seller" : "/buyer";

  // Determine whether to hide extras based on current path
  const hideExtrasOnPath =
    location.pathname.startsWith("/seller") ||
    location.pathname.startsWith("/admin");

  /* ---------- FIX CONTENT OFFSET ---------- */
  useLayoutEffect(() => {
    const header = document.querySelector(".navbar-custom");
    const main = document.querySelector("#main-content");
    if (header && main) {
      main.style.paddingTop = `${header.offsetHeight}px`;
    }
  }, []);

  /* ---------- SEARCH ---------- */
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    navigate(`/search-results?q=${searchTerm}`);
    setSearchTerm("");
  };

  /* ---------- DROPDOWN HELPERS ---------- */
  const closeMenu = () => {
    clearTimeout(hideTimerRef.current);
    setShowUserMenu(false);
  };

  const startAutoClose = () => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(closeMenu, 3000);
  };

  /* ---------- CLICK OUTSIDE ---------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showUserMenu &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !buttonRef.current.contains(e.target)
      ) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  /* ---------- ESC KEY ---------- */
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <nav
      className="navbar navbar-dark navbar-custom fixed-top shadow-sm px-3"
      style={{
        backgroundColor: "#002266",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        zIndex: 1040,
      }}
    >
      <div className="container-fluid d-flex align-items-center gap-3">
        {/* ---------- LOGO ---------- */}
        <Link to="/" className="navbar-brand d-flex align-items-center gap-2">
          <img src={ImageFile} alt="Logo" height="38" />
          <strong>Afrogebeya</strong>
        </Link>

        {/* ---------- SEARCH (hide only on /seller and /admin paths) ---------- */}
        {!hideExtras && !hideExtrasOnPath && (
          <form
            onSubmit={handleSearch}
            className="d-flex flex-grow-1 mx-2 mx-md-4"
          >
            <input
              className="form-control form-control-sm"
              type="search"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <button className="btn btn-info btn-sm ms-2 px-3 d-none d-sm-inline">
              Search
            </button>
            <button className="btn btn-info btn-sm ms-2 d-sm-none">🔍</button>
          </form>
        )}

        {/* ---------- RIGHT ACTIONS ---------- */}
        {!hideExtras && (
          <div className="d-flex align-items-center gap-3 ms-auto">
            <Link
              to="/cart"
              className="btn btn-outline-light position-relative"
              style={{ width: 38, height: 38, borderRadius: "50%" }}
            >
              <i className="bi bi-cart4"></i>
              {cartCount > 0 && (
                <span className="badge bg-danger position-absolute top-0 start-100 translate-middle">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* ---------- USER MENU ---------- */}
            {isAuthenticated ? (
              <div className="position-relative">
                <button
                  ref={buttonRef}
                  className="btn btn-outline-light btn-sm d-flex align-items-center gap-1"
                  onClick={() => {
                    setShowUserMenu((v) => !v);
                    startAutoClose();
                  }}
                >
                  👤
                  <span className="d-none d-md-inline">{displayName}</span>
                  <i className="bi bi-caret-down-fill small"></i>
                </button>

                {/* ---------- DROPDOWN ---------- */}
                <div
                  ref={dropdownRef}
                  className={`user-dropdown ${showUserMenu ? "open" : ""}`}
                  onMouseEnter={() => clearTimeout(hideTimerRef.current)}
                  onMouseLeave={startAutoClose}
                >
                  {/* <Link to={`${prefix}/messages`} onClick={closeMenu}>
                    📧 Messages
                  </Link> */}
                  <Link to={`${prefix}/notifications`} onClick={closeMenu}>
                    🔔 Notifications
                  </Link>

                  <hr />

                  <Link to={`${prefix}/profile`} onClick={closeMenu}>
                    👤 Profile
                  </Link>

                  {prefix === "/seller" && (
                    <Link to={`${prefix}/my-shop`} onClick={closeMenu}>
                      🏪 My Shop
                    </Link>
                  )}

                  <Link to={`${prefix}/settings`} onClick={closeMenu}>
                    ⚙️ Settings
                  </Link>

                  <hr />

                  <LogoutButton closeMenu={closeMenu} />
                </div>
              </div>
            ) : (
              <>
                <Link to="/login" className="btn btn-outline-light btn-sm">
                  Login
                </Link>
                <Link to="/register" className="btn btn-outline-light btn-sm">
                  Register
                </Link>
              </>
            )}
          </div>
        )}
      </div>

      {/* ---------- INLINE STYLES ---------- */}
      <style>{`
        .user-dropdown {
          position: absolute;
          top: 110%;
          right: 0;
          background: white;
          color: #000;
          border-radius: 8px;
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
          min-width: 240px;
          opacity: 0;
          transform: translateY(-10px);
          pointer-events: none;
          transition: all 0.2s ease;
          z-index: 2000;
        }

        .user-dropdown.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        .user-dropdown a,
        .user-dropdown .logout-btn {
          display: block;
          padding: 10px 16px;
          text-align: center;
          text-decoration: none;
          color: #000;
          background: none;
          border: none;
          width: 100%;
        }

        .user-dropdown a:hover,
        .user-dropdown .logout-btn:hover {
          background: #f1f3f5;
        }

        .logout-btn {
          color: #dc3545;
          font-weight: 500;
        }

        @media (max-width: 576px) {
          .user-dropdown {
            right: -16px;
            left: -16px;
            min-width: auto;
            border-radius: 0 0 12px 12px;
          }
        }
      `}</style>
    </nav>
  );
}

export default Header;
